import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

/**
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 *
 * Handles the return leg of the Emergent-managed Google OAuth flow.
 * Emergent redirects the browser back to `/auth/callback#session_id=xxx`.
 * This component:
 *   1. Reads session_id from the URL fragment (via useLocation().hash — reactive)
 *   2. POSTs it to /api/auth/google/session
 *   3. Stores the returned JWT the same way password login does
 *   4. Redirects to /dashboard (student) or /admin/dashboard (admin)
 */
export default function AuthCallback() {
  const location = useLocation();
  const nav = useNavigate();
  const { refresh } = useAuth();
  // useRef guard prevents double-run under StrictMode (playbook recommendation
  // for the one-time session exchange).
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    const hash = location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      // Not a callback URL — send them home.
      nav("/login", { replace: true });
      return;
    }
    hasProcessed.current = true;
    const sessionId = decodeURIComponent(match[1]);
    // Clear the session_id from the URL bar so it can't be re-used or copied.
    window.history.replaceState(null, "", "/auth/callback");
    (async () => {
      try {
        const r = await api.post("/auth/google/session", { session_id: sessionId });
        localStorage.setItem("tpa_token", r.data.token);
        await refresh();
        toast.success(`Welcome, ${r.data.user.name || r.data.user.email}`);
        nav(r.data.user.role === "admin" ? "/admin/dashboard" : "/dashboard", { replace: true });
      } catch (err) {
        const detail = err?.response?.data?.detail || "Google sign-in failed. Please try again.";
        toast.error(detail);
        nav("/login", { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen grid place-items-center bg-background paper-texture" data-testid="auth-callback-page">
      <div className="text-center space-y-3">
        <div className="mx-auto h-12 w-12 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
        <div className="font-serif text-2xl">Signing you in…</div>
        <div className="text-sm text-muted-foreground max-w-xs">
          Verifying your Google account. This usually takes a second or two.
        </div>
      </div>
    </div>
  );
}
